import React, { useEffect, useRef, useState } from 'react'
import {
  Form,
  FormDataConsumer,
  NumberInput,
  RadioButtonGroupInput,
  TextInput,
  required,
  useNotify,
  useTranslate,
  useRefresh,
} from 'react-admin'
import {
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  TableRow,
  TableCell,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { blue } from 'src/assets/colors'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { DatePickers, DateTimePickers } from 'src/components/uiElements/react-hook-fields'
import { Add, Delete, Place, Save } from '@mui/icons-material'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { LoadingButton } from '@mui/lab'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import dayjs from 'dayjs'
import { CardCommonExpandible } from 'src/components/uiElements/cards/CardCommonExpandible'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { ShowElement } from 'src/components/uiElements/CRUD/ShowElement'
import { getSupplierByProduct } from 'src/components/pages/sales/reservation/helper/getSupplierByProduct'
import { customAwait } from 'src/components/pages/sales/reservation/helper/customAwait'
import { destinationsServices } from 'src/services/external/destination.service'
import { useCardId } from 'src/components/pages/sales/reservation/context/CardIdContext'
import { itemServices } from 'src/services/reservation/item/item.service'
import { productServices } from 'src/services/reservation/product/product.service'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { PRICING } from 'src/components/pages/sales/reservation/logic/common/pricing'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import { useQuery } from 'react-query'
import { INVOICED, CONFIRMED, CANCELLED } from 'src/components/pages/sales/reservation/logic/common/state'
import { tagServices } from 'src/services/reservation/tag/tag.service'
import { FilesCard } from '../common/FilesCard'
import { HasPermission } from 'src/helper/HasPermission'
import { PERMISSION_GET } from 'src/helper/Permission'

const PASSENGER_FIELDS = ['name', 'document_number', 'nationality', 'country']
const NEW_PASSENGER_FIELDS = ['name', 'surname']
const RULE_LIQUIDATION_TYPE = ['WHOLESALER', 'SUPPLIER', 'RETAIL']

export const CarForm = ({
  openModal,
  reservation,
  passengers,
  workUnit,
  product,
  handleClose,
  onSubmit,
  recordProduct,
}) => {
  const notify = useNotify()
  const translate = useTranslate()
  const refresh = useRefresh()
  const filesCardRef = useRef(null)
  const [record, setRecord] = useState(recordProduct || {})
  const [sources, setSources] = useState([])
  const [loadingSources, setLoadingSources] = useState(false)
  const [providers, setProviders] = useState([])
  const [currencies, setCurrencies] = useState([])
  const [currenciesTo, setCurrenciesTo] = useState([])
  const [loadingCurrencies, setLoadingCurrencies] = useState(false)
  const [tags, setTags] = useState([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [timeoutTags, setTimeoutTags] = useState()
  const [destinations, setDestinations] = useState([[], []])
  const [loadingDestinations, setLoadingDestinations] = useState([false, false])
  const [timeoutDestinations, setTimeoutDestinations] = useState([null, null])
  const [aditionalPassengers, setAditionalPassengers] = useState([])
  const [loadingLiquidation, setLoadingLiquidation] = useState(false)
  const [loadingButton, setLoadingButton] = useState(false)
  const [wholesalerLiquidation, setWholesalerLiquidation] = useState(null)
  const [retailLiquidation, setRetailLiquidation] = useState(null)
  const [supplierLiquidation, setSupplierLiquidation] = useState(null)
  const [loadingSupplier, setLoadingSupplier] = useState(false)

  const { cardId } = useCardId()
  const carsRenters = useQuery('ext-car-renter', businessRuleServices.getRenterCar)

  useEffect(() => {
    getSources()
    getCurrencies()
    getTags()
    setRecord({
      number: reservation?.['number'],
      client: reservation?.['business_unit_relation']?.['long_name'],
      amount: 0,
      round_trip: true,
      ...recordProduct || {}
    })

    const providersAux = reservation?.['product']
      ?.find((elem) => elem.ref_table_detail === 'car_detail')
      ?.['supplier_relation']?.map((elem) => {
        const [key, value] = Object.entries(elem)[0]
        return { id: key, name: value }
      })

    ;(async () => {
      let providers = []
      if (!providersAux || (providersAux && providersAux.length === 0)) {
        try {
          setLoadingSupplier(true)
          providers = await getSupplierByProduct(product)
        } catch (error) {
        } finally {
          setLoadingSupplier(false)
        }
      }
      setProviders(providersAux || providers)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      if (recordProduct) {
        Promise.all(([
          getSourcesService(),
          getDestinationsService(recordProduct?.pick_up_address?.split(',')?.[0]),
          getDestinationsService(recordProduct?.drop_off_address?.split(',')?.[0])
        ])).then(([sources, origin, destination]) => {
          const sourceFind = sources.find((source) => source.id === recordProduct?.source_id)
          const destinationOriginFind = (
            origin?.data?.find((elem) => elem.category === 'Ciudades')?.data || []
          ).find((destination) => destination.label === recordProduct?.pick_up_address)

          const destinationDestinationFind = (
            destination?.data?.find((elem) => elem.category === 'Ciudades')?.data || []
          ).find((destination) => destination.label === recordProduct?.drop_off_address)

          const cancelInfoRelationFind = recordProduct?.cancel_info_relation.find(
            (cancel) => !cancel?.is_client_cancel
          )

          const isRountTrip =
            recordProduct?.destination !== null &&
            recordProduct?.origin !== null &&
            recordProduct?.origin === recordProduct?.destination

          setRecord({
            ...reservation,
            client: reservation?.['business_unit_relation']?.['long_name'],
            ...recordProduct,
            source: sourceFind,
            origin: destinationOriginFind,
            destination: destinationDestinationFind,
            cancellation_date: cancelInfoRelationFind?.date,
            cancellation_amount: cancelInfoRelationFind?.amount,
            net_price: recordProduct?.pricing_relation?.[0]?.net_price,
            pax_titular: recordProduct?.pax_relation?.[0]?.id,
            pax_info: recordProduct?.pax_relation?.[0]?.id,
            round_trip: isRountTrip,
          })
        })
      }
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordProduct])

  const getDestinationsService = async (name) => {
    try {
      const response = await destinationsServices.getDestinationCarByName(name)
      return response
    } catch (error) {
      throw error
    }
  }

  const getSourcesService = async () => {
    try {
      const response = await reservationServices.getSources()
      return response
    } catch (error) {
      throw error
    }
  }

  const getBusinessUnitService = async (businessUnitId) => {
    try {
      const response = await businessUnitServices.getById(businessUnitId)
      return response
    } catch (error) {
      throw error
    }
  }

  const getSources = async () => {
    try {
      setLoadingSources(true)
      const response = await getSourcesService()
      setSources(response)
      setLoadingSources(false)
    } catch (error) {
      setSources([])
      setLoadingSources(false)
    }
  }

  const getCurrencies = async () => {
    try {
      setLoadingCurrencies(true)
      if (!reservation?.business_unit_id){
        setCurrencies([])
        return
      }
      const response = await getBusinessUnitService(reservation?.business_unit_id)
      if (response) {
        const allCurrencies = [...response.currency_relation, ...response.currency_inheritance]
        const currenciesAux = []
        const currenciesToAux = []
        allCurrencies?.forEach((currency) => {
          if (!currenciesAux.find((elem) => elem.id === currency.currency_id)) {
            currenciesAux.push(currency.currency_relation)
          }
          if (
            !currenciesToAux.find(
              (elem) =>
                elem.currency_id === currency.currency_id && elem.currency_to_id === currency.currency_to_id
            )
          ) {
            currenciesToAux.push(currency)
          }
        })
        setCurrencies(currenciesAux)
        setCurrenciesTo(currenciesToAux)
      }
      setLoadingCurrencies(false)
    } catch (error) {
      setCurrencies([])
      setLoadingCurrencies(false)
    }
  }

  const getTags = async (name) => {
    clearTimeout(timeoutTags)
    try {
      const timeout = setTimeout(async () => {
        setLoadingTags(true)
        const tagsResponse = await tagServices.getTagsByWorkUnit(reservation?.['work_unit_id'], name)
        setTags(tagsResponse)
        setLoadingTags(false)
      }, 1000)
      setTimeoutTags(timeout)
    } catch (error) {
      setTags([])
      setLoadingTags(false)
    }
  }

  const getDestinations = async (name, index) => {
    clearTimeout(timeoutDestinations[index])
    const loadingDestinationsAux = [...loadingDestinations]
    const destinationsAux = [...destinations]

    if (!name || name.length < 3) {
        destinationsAux.splice(index, 1, [])
        setDestinations(destinationsAux)
        return;  // Detener la ejecución si el nombre es menor de 3 caracteres
    }

    try {
      const timeout = setTimeout(async () => {
        loadingDestinationsAux.splice(index, 1, true)
        setLoadingDestinations(loadingDestinationsAux)
        const response = await getDestinationsService(name)
        let filteredDestinations = response?.data?.find((elem) => elem.category === 'Aeropuertos')?.data || []
        // filteredDestinations = filteredDestinations.filter((elem) => elem.iata !== null)
        destinationsAux.splice(
          index,
          1,
          filteredDestinations
        )
        setDestinations(destinationsAux)
        loadingDestinationsAux.splice(index, 1, false)
        setLoadingDestinations(loadingDestinationsAux)
      }, 1000)
      const timeoutDestinationsAux = [...timeoutDestinations]
      timeoutDestinationsAux.splice(index, 1, timeout)
      setTimeoutDestinations(timeoutDestinationsAux)
    } catch (error) {
      destinationsAux.splice(index, 1, [])
      setDestinations([[], []])
      loadingDestinationsAux.splice(index, 1, false)
      setLoadingDestinations(loadingDestinationsAux)
    }
  }


  const onClickCalculateLiquidationAux = async (formData, rule_liquidation_type, currency_to_show = null) => {
    let productVar = null
    if (recordProduct) {
      productVar = await productServices.getProductByWorkUnitWithId(product?.product_by_work_unit_id)
    }

    let renter = null
    if (formData?.renter) {
      if (carsRenters?.data?.length > 0) {
        const renterSelected = carsRenters?.data?.find((renter) => renter?.id === formData?.renter)
        if (renterSelected) {
          renter = renterSelected?.short_name
        }
      }
    }

    const data = {
      token_workunit: workUnit?.token,
      token_product: product?.token_id || productVar?.token_id,
      hash_product: product?.product_relation?.hash_product || productVar?.product_relation?.hash_product,
      rules_parameters: {
        business_unit_token: reservation?.business_unit_relation?.token,
        source: formData.source?.short_name,
        destination: formData.destination?.iata,
        destination_type: Number(formData.destination_type),
        renter: renter,
        checkin: dayjs(formData.date_in).format('YYYY-MM-DD'),
        rule_type: 'LIQUIDATION',
        rule_liquidation_type,
      },
      data: {
        ...PRICING,
        net_price: formData.net_price,
        final_sale: formData.final_sale || 0,
        currency_to_show: currency_to_show || {}
      },
    }

    try {
      const response = await reservationServices.applyLiquidationRule(data)
      return {
        liquidation: response,
        rule_liquidation_type,
        requestRule: data,
        message: '',
        messsageArg: '',
        rule_applied_info: response?.rules
      }
    } catch (error) {
      return {
        liquidation: {},
        rule_liquidation_type,
        requestRule: data,
        message: 'ra.message.rule_not_apply',
        messsageArg: 'liquidación',
      }
    }
  }

  const onClickCalculateLiquidation = async (formData) => {
    try {
      setLoadingLiquidation(true)

      const currency_to_show = {}
      currencies
        .forEach((currency) => {
          if (currency.id !== formData.currency_id) {
            const currencyToRelation = currenciesTo.find(
              (currencyTo) =>
                (currencyTo.currency_id === formData.currency_id &&
                  currencyTo.currency_to_id === currency.id) ||
                (currencyTo.currency_id === currency.id &&
                  currencyTo.currency_to_id === formData.currency_id &&
                  currencyTo.is_reverse)
            )
            if (currencyToRelation) {
              currency_to_show[currency.short_name] =
                currencyToRelation.currency_id === formData.currency_id
                  ? currencyToRelation.value
                  : Math.round((1 / currencyToRelation.value) * 10000) / 10000
            }
          } else {
            currency_to_show[currency.short_name] = 1
          }
        })
      const responses = await Promise.all(
        RULE_LIQUIDATION_TYPE.map((type) => onClickCalculateLiquidationAux(formData, type, currency_to_show))
      )

      const pricing = { ...formData.pricing }
      responses.forEach((elem) => {
        const rules = elem?.liquidation?.data?.item?.rules
        if (rules) {
          elem['liquidation']['rules'] = rules
        }
        pricing[`liquidation_${elem.rule_liquidation_type.toLowerCase()}`] = { ...elem }
      })
      pricing.currency_origin = currencies.find((elem) => elem.id === formData.currency_id)?.short_name || ''

      setWholesalerLiquidation(
        pricing.liquidation_wholesaler.liquidation.rules?.customVars?.filter((elem) => elem.show) || []
      )

      setRetailLiquidation(
        pricing.liquidation_retail.liquidation.rules?.customVars?.filter((elem) => elem.show) || []
      )

      setSupplierLiquidation(
        pricing.liquidation_supplier.liquidation.rules?.customVars?.filter((elem) => elem.show) || []
      )

      pricing.currency_to_show = currency_to_show

      setRecord({
        ...formData,
        pricing,
      })

      return {
        ...formData,
        pricing,
      }
    } catch (error) {
      console.log('onClickCalculateLiquidation error', error)
    } finally {
      setLoadingLiquidation(false)
    }
  }

  const onChangeOrigin = (formData, origin) => {
    if (formData.round_trip) {
      setRecord({
        ...formData,
        origin,
        destination: origin,
        pick_up_address: origin,
        drop_off_address: origin,
      })
    }
  }

  const onChangeTypeOrigin = (formData, typeOrigin) => {
    if (formData.round_trip) {
      setRecord({
        ...formData,
        type_origin: typeOrigin,
        type_destination: typeOrigin,
      })
    }
  }

  const onChangeRoundTrip = (formData, roundTrip) => {
    setRecord({
      ...formData,
      round_trip: roundTrip === 'true' ? true : false,
      destination: roundTrip === 'true' ? formData.origin : '',
      type_destination: roundTrip === 'true' ? formData.type_origin : '',
    })
  }

  const getAvailablePassengers = (formData, filterAditional = false, aditionalPassengerId = null) => {
    let passengerIds = [formData?.pax_info]

    if (filterAditional) {
      passengerIds = [...passengerIds, ...aditionalPassengers.map((elem) => elem.id && !elem.isNew)]
    }

    if (aditionalPassengerId) {
      passengerIds = passengerIds.filter((elem) => elem !== aditionalPassengerId)
    }

    return passengers.filter((passenger) => !passengerIds.includes(passenger.id))
  }

  const onChangeTitularPassenger = (passenger, formData) => {
    const formDataAux = JSON.parse(JSON.stringify(formData))
    formDataAux.pax_info = passenger
    setRecord(formDataAux)

    const aditionalPassengersAux = [...aditionalPassengers]
    const indexPassenger = aditionalPassengersAux.findIndex((elem) => elem.id === passenger)
    if ((!isNaN(passenger) || !passenger.includes('new')) && indexPassenger > -1) {
      aditionalPassengersAux.splice(indexPassenger, 1)
      setAditionalPassengers(aditionalPassengersAux)
    }
  }

  const onChangePassenger = (value, index, field) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux[index][field] = value
    setAditionalPassengers(aditionalPassengersAux)
  }

  const onChangeAditionalPassenger = (passenger, index) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux[index] = passengers.find((elem) => elem.id === passenger)
    setAditionalPassengers(aditionalPassengersAux)
  }

  const addPassenger = (isNew = false) => {
    const index =
      parseInt(
        aditionalPassengers
          ?.filter((elem) => elem.isNew)
          ?.map((elem) => elem.id.split('_')[1])
          ?.reverse()?.[0] || -1
      ) + 1
    setAditionalPassengers([
      ...aditionalPassengers,
      {
        isNew,
        id: `new_${index}`,
      },
    ])
  }

  const removePassenger = (index) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux.splice(index, 1)
    setAditionalPassengers(aditionalPassengersAux)
  }

  const getPassengerInfo = (passengerId, field) => {
    const allPassengers = [
      ...passengers,
      ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
    ]
    const passenger = allPassengers.find((elem) => elem.id === passengerId)
    if (passenger && field === 'name') {
      return `${passenger.name} ${passenger.surname}`
    } else {
      return passenger?.[field] || ''
    }
  }

  const getPassenger = (passengerId) => {
    if (!isNaN(passengerId)) {
      return passengerId
    } else {
      const passenger = { ...aditionalPassengers.find((elem) => elem.id === passengerId) }
      delete passenger.id
      delete passenger.isNew
      return passenger
    }
  }

  const postSave = async (data) => {
    const paxTitular = getPassenger(data.pax_titular)
    let paxInfo = [...aditionalPassengers.map((elem) => getPassenger(elem.id))]

    if (!isNaN(data.pax_info)) {
      paxInfo.unshift(data.pax_info)
    }

    let productVar = null
    if (recordProduct) {
      productVar = await productServices.getProductByWorkUnitWithId(product?.product_by_work_unit_id)
    }
    let provider = null
    let company_code = null
    if (data?.renter) {
      if (carsRenters?.data?.length > 0) {
        const renterSelected = carsRenters?.data?.find((renter) => renter?.id === data?.renter)
        if (renterSelected) {
          provider = renterSelected?.long_name
          company_code = renterSelected?.short_name
        }
      }
    }

    const dataToSend = {
      ...data,
      provider: provider,
      company_code: company_code,
      product_id: product?.product_id,
      product_token: product?.token_id || productVar?.token_id,
      ref_table_detail: 'car_detail',
      reservation_id: reservation.id,
      tag_relation: data.tag_relation.map((elem) => ({ id: elem.id })),
      pax_info: paxInfo,
      pax_titular: paxTitular,
      cancellation_date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
      date_in: data.date_in ? dayjs(data.date_in).format('YYYY-MM-DD') : null,
      date_out: data.date_out ? dayjs(data.date_out).format('YYYY-MM-DD') : null,
      time_in: data.date_in ? dayjs(data.date_in).format('HHmm') : null,
      time_out: data.date_out ? dayjs(data.date_out).format('HHmm') : null,
      source_id: data.source.id,
      origin: data.origin.iata,
      pick_up_address: data.origin.label,
      drop_off_address: data.destination.label,
      destination: data.destination.iata,
      destination_type: Number(data.destination_type)
    }

    const dataToSendEdit = {
      ...data,
      id: data?.item_id,
      product_id: product?.product_id,
      product_token: product?.token_id || productVar?.token_id,
      ref_table_detail: 'car_detail',
      reservation_id: reservation.id,
      tag_relation: data.tag_relation.map((elem) => ({ id: elem.id })),
      pax_info: paxInfo,
      pax_titular: paxTitular,
      cancellation_date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
      destination_type: Number(data.destination_type),
      source_id: data.source.id,
      cancel_info_relation: data?.cancel_info_relation?.map((cancel) => {
        if (!cancel?.is_client_cancel) {
          return {
            ...cancel,
            date: data?.cancellation_date,
            amount: data?.cancellation_amount,
          }
        }
        return cancel
      }),
      detail: {
        ...data,
        code: data.code,
        name: data.name,
        id: data?.id_detail,
        cancellation_date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
        date_in: data.date_in ? dayjs(data.date_in).format('YYYY-MM-DD') : null,
        date_out: data.date_out ? dayjs(data.date_out).format('YYYY-MM-DD') : null,
        time_in: data.date_in ? dayjs(data.date_in).format('HHmm') : null,
        time_out: data.date_out ? dayjs(data.date_out).format('HHmm') : null,
        origin: data.origin.iata,
        destination: data.destination.iata,
        pick_up_address: data.origin.label,
        drop_off_address: data.destination.label,
        source: data.source.label,
        coverage: data.coverage,
        type_origin: data.type_origin,
        type_destination: data.type_destination,
        supplier_id: data.supplier_id,
        provider: provider,
        company_code: company_code,
      },
    }

    setLoadingButton(true)
    try {
      let response
      if (recordProduct) {
        const liquidationCalculate = await onClickCalculateLiquidation(dataToSendEdit)
        if (liquidationCalculate) {
          dataToSendEdit['pricing_relation'] = [
            {
              ...liquidationCalculate?.pricing_relation?.[0],
              ...liquidationCalculate?.pricing,
            },
          ]
        }
        response = await itemServices.update_item_and_detail(dataToSendEdit.id, dataToSendEdit)
      } else {
        const liquidationCalculate = await onClickCalculateLiquidation(data)
        dataToSend.pricing = {
          ...(liquidationCalculate?.pricing || data.pricing || {}),
          net_price: data.net_price,
          final_sale: data.final_sale || 0,
          currency_id: data.currency_id,
        }
        response = await reservationServices.createItemManual(dataToSend)

        if (filesCardRef?.current) {
          filesCardRef.current.uploadPendingFiles(response.hash_)
        }
      }

      setLoadingButton(false)
      onSubmit && onSubmit(response, cardId)
      if (recordProduct) {
        notify(translate('ra.message.success_edit_item'), { type: 'success' })
      } else {
        notify(translate('ra.message.success_create_item'), { type: 'success' })
      }
    } catch (error) {
      if (!recordProduct) {
        notify(translate('ra.message.error_create_item'), { type: 'error' })
      } else {
        notify(translate('ra.message.error_update_item'), { type: 'error' })
      }
      setLoadingButton(false)
    } finally {
      customAwait([refresh])
      handleClose()
    }
  }

  const onChangeNetPrice = (formData, net_price) => {
    setRecord({
      ...formData,
      net_price,
      cancellation_amount: net_price,
    })
  }

  const getText = (item, optionsText) => (optionsText ? optionsText.map((option) => item[option]) : item.name)

  const renderOption = (props, option) => (
    <li {...props}>
      <Chip
        variant='outlined'
        label={getText(option, ['long_name'])}
        avatar={option?.symbol && <Avatar>{option?.symbol}</Avatar>}
        style={{ backgroundColor: option?.color, cursor: 'pointer' }}
        size='small'
      />
    </li>
  )

  return (
    <Form id='post_create_form' warnWhenUnsavedChanges onSubmit={postSave} record={record}>
      <Dialog open={openModal} maxWidth='lg' fullWidth={true} className='dialog-product'>
        <DialogTitle>{`${recordProduct ? translate('ra.action.edit') : translate('ra.action.add')} ${translate(
          'ra.title.car'
        )}`}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={recordProduct ? 12 : 9}>
                {!recordProduct && (
                  <CardCommon title={translate('ra.title.passenger')} style={{ marginBottom: 12 }}>
                    <FormDataConsumer>
                      {({ formData }) => (
                        <Grid container spacing={3}>
                          {formData.pax_info && !isNaN(formData.pax_info) && (
                            <Grid item xs={12} className='passenger-container'>
                              {PASSENGER_FIELDS.map((field, j) => (
                                <EditInputsWrap
                                  label={translate(`ra.field.${field}`)}
                                  xs={24}
                                  md={j === 3 ? 2 : 3}
                                  key={j}
                                >
                                  <TextField
                                    label=''
                                    size='small'
                                    fullWidth
                                    disabled
                                    value={getPassengerInfo(formData.pax_info, field)}
                                  />
                                </EditInputsWrap>
                              ))}
                            </Grid>
                          )}
                          {aditionalPassengers.map((passenger, z) => (
                            <Grid item xs={12} key={z} className='passenger-container'>
                              {passenger.isNew
                                ? NEW_PASSENGER_FIELDS.map((field, j) => (
                                    <EditInputsWrap label={translate(`ra.field.${field}`)} md={3} key={j}>
                                      <TextField
                                        label=''
                                        size='small'
                                        fullWidth
                                        onChange={(e) => onChangePassenger(e.target.value, z, field)}
                                      />
                                    </EditInputsWrap>
                                  ))
                                : PASSENGER_FIELDS.map((field, j) => (
                                    <EditInputsWrap
                                      label={translate(`ra.field.${field}`)}
                                      lg={3}
                                      md={j === 3 ? 2 : 3}
                                      key={j}
                                    >
                                      {field === 'name' ? (
                                        <Select
                                          fullWidth
                                          size='small'
                                          value={passenger.id}
                                          onChange={(e) => onChangeAditionalPassenger(e.target.value, z)}
                                        >
                                          {getAvailablePassengers(formData, passenger.id).map(
                                            (passenger, i) => (
                                              <MenuItem key={i} value={passenger.id}>
                                                {getPassengerInfo(passenger.id, field)}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      ) : (
                                        <TextField
                                          label=''
                                          size='small'
                                          fullWidth
                                          disabled
                                          value={getPassengerInfo(passenger.id, field)}
                                        />
                                      )}
                                    </EditInputsWrap>
                                  ))}
                              <Grid
                                item
                                xs={passenger.isNew ? 6 : 1}
                                style={{ paddingTop: 25, textAlign: 'end' }}
                              >
                                <IconButton onClick={() => removePassenger(z)} color='error'>
                                  <Delete />
                                </IconButton>
                              </Grid>
                            </Grid>
                          ))}
                          <Grid item xs={24}>
                            <Button
                              variant='standard'
                              startIcon={<Add />}
                              style={{ color: blue }}
                              onClick={() => addPassenger(true)}
                            >
                              Crear un pasajero
                            </Button>
                            <Button
                              variant='standard'
                              startIcon={<Add />}
                              style={{ color: blue }}
                              onClick={() => addPassenger()}
                            >
                              Agregar otro pasajero
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </FormDataConsumer>
                  </CardCommon>
                )}
                <CardCommon title={translate('ra.title.reserve_information')} style={{ marginBottom: 12 }}>
                  <Grid container spacing={3}>
                    <EditInputsWrap label={translate('ra.field.number')}>
                      <TextInput source='number' label='' fullWidth disabled />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.client')}>
                      <TextInput source='client' label='' fullWidth disabled />
                    </EditInputsWrap>
                    {!recordProduct && (
                      <EditInputsWrap label={translate('ra.field.amount')}>
                        <TextInput source='net_price' label='' fullWidth disabled />
                      </EditInputsWrap>
                    )}
                    <EditInputsWrap label={translate('ra.field.status')} required>
                      <SelectInput
                        source='status'
                        optionsText={['name']}
                        optionValue='key'
                        options={[
                          { key: CONFIRMED, name: 'CONFIRMADO' },
                          { key: CANCELLED, name: 'CANCELADO OFFLINE' },
                          { key: INVOICED, name: 'EMITIDA' },
                        ]}
                        label=''
                        fullWidth
                        validate={[required()]}
                        defaultValue={INVOICED}
                      />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.source')} required>
                      {loadingSources ? (
                        <Skeleton variant='rectangular' height={40} />
                      ) : (
                        <AutocompleteInput
                          source='source'
                          options={sources}
                          validate={[required()]}
                          optionsText={['long_name']}
                          disableClearable
                          defaultValue={sources.find(elem => elem.long_name === 'OFFLINE')}
                        />
                      )}
                    </EditInputsWrap>
                    {!recordProduct && (
                      <EditInputsWrap label={translate('ra.field.passenger')} required>
                        <SelectInput
                          source='pax_titular'
                          optionsText={['name', 'surname']}
                          optionValue='id'
                          options={[
                            ...passengers,
                            ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
                          ]}
                          label=''
                          fullWidth
                          validate={[required()]}
                          separator=' '
                        />
                      </EditInputsWrap>
                    )}

                    <Typography
                      variant='h6'
                      component='div'
                      color={blue}
                      style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                    >
                      {translate('ra.title.provider_info')}
                    </Typography>
                    <EditInputsWrap label={translate('ra.field.provider')} required>
                      {loadingSupplier ? (
                        <Skeleton variant='rectangular' height={40} />
                      ) : (
                        <SelectInput
                          source='supplier_id'
                          optionsText={['name']}
                          optionValue='id'
                          options={providers}
                          label=''
                          fullWidth
                          showSearch
                          validate={[required()]}
                        />
                      )}
                    </EditInputsWrap>
                    <EditInputsWrap label={`${translate('ra.field.locator')} 1`} required>
                      <TextInput source='code' label='' fullWidth validate={[required()]} />
                    </EditInputsWrap>
                    <EditInputsWrap label={`${translate('ra.field.locator')} 2`}>
                      <TextInput source='provider_code' label='' fullWidth />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.currency')} required>
                      {loadingCurrencies ? (
                        <Skeleton variant='rectangular' height={40} />
                      ) : (
                        <SelectInput
                          source='currency_id'
                          optionsText={['long_name']}
                          optionValue='id'
                          options={currencies}
                          label=''
                          fullWidth
                          validate={[required()]}
                        />
                      )}
                    </EditInputsWrap>
                    {!recordProduct && (
                      <>
                        <FormDataConsumer>
                          {({ formData }) => (
                            <EditInputsWrap label={translate('ra.field.net_provider')} required>
                              <NumberInput
                                source='net_price'
                                label=''
                                fullWidth
                                validate={[required()]}
                                onChange={(e) => onChangeNetPrice(formData, e.target.value)}
                              />
                            </EditInputsWrap>
                          )}
                        </FormDataConsumer>
                        <EditInputsWrap label={translate('ra.field.final_sale')}>
                          <NumberInput
                            source='final_sale'
                            label=''
                            fullWidth
                          />
                        </EditInputsWrap>
                        <Typography
                          variant='h6'
                          component='div'
                          color={blue}
                          style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                        >
                          {translate('ra.title.tags')}
                        </Typography>
                        <EditInputsWrap label={translate('ra.field.add_tags')} md={12} className='small'>
                          {loadingTags ? (
                            <Skeleton variant='rectangular' height={40} />
                          ) : (
                            <AutocompleteInput
                              source='tag_relation'
                              multiple
                              options={tags}
                              optionsText={['long_name']}
                              renderOption={renderOption}
                              renderTags
                            />
                          )}
                        </EditInputsWrap>
                      </>
                    )}
                  </Grid>
                </CardCommon>
                <CardCommon title={translate('ra.title.car_information')} style={{ marginBottom: 12 }}>
                  <Grid container spacing={3}>
                    <EditInputsWrap label={translate('ra.field.renter')} required>
                      <SelectInput
                        source='renter'
                        optionsText={['long_name']}
                        optionValue='id'
                        options={carsRenters?.data || []}
                        label=''
                        fullWidth
                        validate={[required()]}
                      />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.car_name')}>
                      <TextInput source='name' label='' fullWidth />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.coverage')}>
                      <TextInput source='coverage' label='' fullWidth />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.destination_type')} required md={12}>
                      <RadioButtonGroupInput
                        source='destination_type'
                        choices={[
                          { id: 2, name: translate('ra.destination_type.national') },
                          { id: 1, name: translate('ra.destination_type.international') },
                        ]}
                        validate={[required()]}
                      />
                    </EditInputsWrap>
                    <FormDataConsumer>
                      {({ formData }) => (
                        <>
                          <EditInputsWrap label={translate('ra.field.pick_up')} required>
                            <AutocompleteInput
                              source='origin'
                              options={destinations[0]}
                              loading={loadingDestinations[0]}
                              optionValue='label'
                              optionsText={['label']}
                              onSearch={(value) => getDestinations(value, 0)}
                              startAdornment={<Place />}
                              validate={[required()]}
                              onChange={(e) => onChangeOrigin(formData, e.target.value)}
                            />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.pickup_type')}>
                            <SelectInput
                              source='type_origin'
                              optionsText={['name']}
                              optionValue='name'
                              options={[
                                { name: 'AEROPUERTO' },
                                { name: 'PUERTO' },
                                { name: 'OFICINA CIUDAD' },
                                { name: 'TREN' },
                              ]}
                              label=''
                              fullWidth
                              onChange={(e) => onChangeTypeOrigin(formData, e.target.value)}
                            />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.check_in_pick_up')} required>
                            <DateTimePickers source='date_in' validate={[required()]} />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.round_trip')} required md={12}>
                            <RadioButtonGroupInput
                              source='round_trip'
                              choices={[
                                { id: true, name: 'Sí' },
                                { id: false, name: 'No' },
                              ]}
                              validate={[required()]}
                              onChange={(e, value) => onChangeRoundTrip(formData, value)}
                            />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.drop_off')} required>
                            <AutocompleteInput
                              source='destination'
                              options={destinations[1]}
                              loading={loadingDestinations[1]}
                              optionValue='label'
                              optionsText={['label']}
                              onSearch={(value) => getDestinations(value, 1)}
                              resetOnLoad={false}
                              startAdornment={<Place />}
                              validate={[required()]}
                              disabled={formData.round_trip}
                            />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.dropoff_type')}>
                            <SelectInput
                              source='type_destination'
                              optionsText={['name']}
                              optionValue='name'
                              options={[
                                { name: 'AEROPUERTO' },
                                { name: 'PUERTO' },
                                { name: 'OFICINA CIUDAD' },
                                { name: 'TREN' },
                              ]}
                              label=''
                              fullWidth
                              disabled={formData.round_trip}
                            />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.check_in_drop_off')} required>
                            <DateTimePickers
                              source='date_out'
                              validate={[required()]}
                              minDateTime={formData?.date_in ? dayjs(formData.date_in) : null}
                            />
                          </EditInputsWrap>
                          {!recordProduct && (
                            <EditInputsWrap label={translate('ra.field.titular_passenger')}>
                              <SelectInput
                                source='pax_info'
                                optionsText={['name', 'surname']}
                                optionValue='id'
                                options={[
                                  ...passengers,
                                  ...aditionalPassengers.filter(
                                    (elem) => elem.isNew && elem.name && elem.surname
                                  ),
                                ]}
                                label=''
                                fullWidth
                                separator=' '
                                defaultValue={passengers?.find(elem => elem.is_titular)?.id || passengers?.[0]?.id || ''}
                                onChange={(e) => onChangeTitularPassenger(e.target.value, formData)}
                              />
                            </EditInputsWrap>
                          )}
                          <Grid item xs={8} />

                          {!recordProduct &&
                            formData.pax_info &&
                            PASSENGER_FIELDS.map((field, j) => (
                              <EditInputsWrap label={translate(`ra.field.${field}`)} md={3} key={j}>
                                <TextField
                                  label=''
                                  size='small'
                                  fullWidth
                                  disabled
                                  value={getPassengerInfo(formData.pax_info, field)}
                                />
                              </EditInputsWrap>
                            ))}

                          <Typography
                            variant='h6'
                            component='div'
                            color={blue}
                            style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                          >
                            {translate('ra.title.cancellation_cost')}
                          </Typography>
                          <Typography
                            variant='h6'
                            component='div'
                            style={{ marginLeft: 12, marginBottom: 10, width: '100%', fontSize: '1em' }}
                          >
                            {translate('ra.title.cancellation_cost_provider')}
                          </Typography>
                          <EditInputsWrap label={translate('ra.field.date')} required>
                            <DatePickers
                              source='cancellation_date'
                              required
                              maxDate={formData?.date_in ? dayjs(formData.date_in) : null}
                              viewsShow={false}
                              defaultValue={dayjs()}
                            />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.amount')} required>
                            <NumberInput
                              source='cancellation_amount'
                              label=''
                              fullWidth
                              validate={[required()]}
                            />
                          </EditInputsWrap>
                        </>
                      )}
                    </FormDataConsumer>
                  </Grid>
                </CardCommon>
                <HasPermission resource='file-upload' permission={PERMISSION_GET}>
                  <CardCommon title={translate('ra.title.files')}>
                    <FilesCard ref={filesCardRef} recordProduct={recordProduct} />
                  </CardCommon>
                </HasPermission>
              </Grid>
              {!recordProduct && (
                <Grid item xs={3}>
                  <Grid position='sticky' top={0}>
                    <FormDataConsumer>
                      {({ formData }) => {
                        const isDisabled =
                          loadingLiquidation ||
                          !workUnit ||
                          !product ||
                          !formData.source ||
                          !formData.destination ||
                          !formData.destination_type ||
                          !formData.renter ||
                          !formData.date_in ||
                          !(formData.net_price !== null && formData.net_price !== undefined) ||
                          !formData.currency_id
                        return (
                          <>
                            <Grid display='flex' justifyContent='flex-end' marginBottom='12px'>
                              <Tooltip
                                title='Para calcular la liquidación, es necesario rellenar los siguientes campos: Fuente, Moneda, Neto proveedor, Rentadora, Destino, Tipo de destino y Fecha Pick Up'
                                className='tooltip'
                                placement='top'
                              >
                                <Icon>info</Icon>
                              </Tooltip>
                              <Button
                                variant='contained'
                                disabled={isDisabled}
                                onClick={() => onClickCalculateLiquidation(formData)}
                              >
                                {translate('ra.action.calculate_liquidation')}
                              </Button>
                            </Grid>
                            <CardCommonExpandible
                              title={translate('ra.title.liquidation_wholesaler')}
                              className='small-fields'
                              style={{ marginBottom: 12 }}
                            >
                              <Grid container spacing={3}>
                                {formData?.pricing?.liquidation_wholesaler?.messsageArg?.length > 0 ? (
                                  <Typography padding={'20px'} style={{ color: 'red' }}>
                                    {translate(formData?.pricing?.liquidation_wholesaler?.message, {
                                      rule: formData?.pricing?.liquidation_wholesaler?.messsageArg,
                                    })}
                                  </Typography>
                                ) : wholesalerLiquidation && wholesalerLiquidation.length > 0 ? (
                                  wholesalerLiquidation.map((item, index) => (
                                    <TableRow key={index} style={{ display: 'flex', flex: '1 1 100%' }}>
                                      <TableCell style={{ flex: 1, alignItems: 'center', display: 'flex' }}>
                                        {item?.description || item.name}
                                      </TableCell>
                                      <TableCell>
                                        <ShowElement
                                          data={numberFormatterWithDecimalAndThousand(item.value)}
                                          onlyValue
                                          blueText
                                          monetarySymbol={
                                            currencies.find((elem) => elem.id === formData.currency_id)
                                              ?.symbol || ''
                                          }
                                          styleValue={{
                                            fontSize: '16px',
                                            fontFamily: "'Roboto Condensed', sans-serif",
                                          }}
                                          source={undefined}
                                          label={undefined}
                                          date={undefined}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <Typography padding={'20px'}>
                                    {translate('ra.notification.liquidation_rule_invalid')}
                                  </Typography>
                                )}
                              </Grid>
                            </CardCommonExpandible>
                            <CardCommonExpandible
                              title={translate('ra.title.liquidation_retail')}
                              className='small-fields'
                              style={{ marginBottom: 12 }}
                            >
                              <Grid container spacing={3}>
                                {formData?.pricing?.liquidation_retail?.messsageArg?.length > 0 ? (
                                  <Typography padding={'20px'} style={{ color: 'red' }}>
                                    {translate(formData?.pricing?.liquidation_retail?.message, {
                                      rule: formData?.pricing?.liquidation_retail?.messsageArg,
                                    })}
                                  </Typography>
                                ) : retailLiquidation && retailLiquidation.length > 0 ? (
                                  retailLiquidation.map((item, index) => (
                                    <TableRow key={index} style={{ display: 'flex', flex: '1 1 100%' }}>
                                      <TableCell style={{ flex: 1, alignItems: 'center', display: 'flex' }}>
                                        {item?.description || item.name}
                                      </TableCell>
                                      <TableCell>
                                        <ShowElement
                                          data={numberFormatterWithDecimalAndThousand(item.value)}
                                          onlyValue
                                          blueText
                                          monetarySymbol={
                                            currencies.find((elem) => elem.id === formData.currency_id)
                                              ?.symbol || ''
                                          }
                                          styleValue={{
                                            fontSize: '16px',
                                            fontFamily: "'Roboto Condensed', sans-serif",
                                          }}
                                          source={undefined}
                                          label={undefined}
                                          date={undefined}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <Typography padding={'20px'}>
                                    {translate('ra.notification.liquidation_rule_invalid')}
                                  </Typography>
                                )}
                              </Grid>
                            </CardCommonExpandible>
                            <CardCommonExpandible
                              title={translate('ra.title.liquidation_supplier')}
                              className='small-fields'
                              style={{ marginBottom: 12 }}
                            >
                              <Grid container spacing={3}>
                                {formData?.pricing?.liquidation_supplier?.messsageArg?.length > 0 ? (
                                  <Typography padding={'20px'} style={{ color: 'red' }}>
                                    {translate(formData?.pricing?.liquidation_supplier?.message, {
                                      rule: formData?.pricing?.liquidation_supplier?.messsageArg,
                                    })}
                                  </Typography>
                                ) : supplierLiquidation && supplierLiquidation.length > 0 ? (
                                  supplierLiquidation.map((item, index) => (
                                    <TableRow key={index} style={{ display: 'flex', flex: '1 1 100%' }}>
                                      <TableCell style={{ flex: 1, alignItems: 'center', display: 'flex' }}>
                                        {item?.description || item.name}
                                      </TableCell>
                                      <TableCell>
                                        <ShowElement
                                          data={numberFormatterWithDecimalAndThousand(item.value)}
                                          onlyValue
                                          blueText
                                          monetarySymbol={
                                            currencies.find((elem) => elem.id === formData.currency_id)
                                              ?.symbol || ''
                                          }
                                          styleValue={{
                                            fontSize: '16px',
                                            fontFamily: "'Roboto Condensed', sans-serif",
                                          }}
                                          source={undefined}
                                          label={undefined}
                                          date={undefined}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <Typography padding={'20px'}>
                                    {translate('ra.notification.liquidation_rule_invalid')}
                                  </Typography>
                                )}
                              </Grid>
                            </CardCommonExpandible>
                          </>
                        )
                      }}
                    </FormDataConsumer>
                    <CardCommon title={translate('ra.title.contact')} className='small-fields'>
                      <Grid container spacing={3}>
                        <EditInputsWrap label={translate('ra.field.name')} md={12}>
                          <TextInput source='contact.name' label='' fullWidth />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.surname')} md={12}>
                          <TextInput source='contact.surname' label='' fullWidth />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.email')} md={12}>
                          <TextInput source='contact.email' label='' fullWidth />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.phone_number')} md={12}>
                          <TextInput source='contact.phone_number' label='' fullWidth />
                        </EditInputsWrap>
                      </Grid>
                    </CardCommon>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions className='dialog-actions'>
            <Grid>
              <FormDataConsumer>
                {({ formData }) => {
                  const isDisabled =
                    loadingLiquidation ||
                    !workUnit ||
                    !product ||
                    !formData.source ||
                    !formData.destination ||
                    !formData.destination_type ||
                    !formData.renter ||
                    !formData.date_in ||
                    !(formData.net_price !== null && formData.net_price !== undefined) ||
                    !formData.currency_id

                  return (
                    <>
                      <LoadingButton
                        loading={loadingButton}
                        variant='contained'
                        startIcon={<Save />}
                        form='post_create_form'
                        type='submit'
                        disabled={!recordProduct && isDisabled}
                      >
                        {recordProduct
                          ? translate('ra.action.save_and_recalculate_liquidation')
                          : translate('ra.action.save_and_calculate_liquidation')}
                      </LoadingButton>

                      {!recordProduct && isDisabled && (
                        <Tooltip
                          title='Para calcular la liquidación, es necesario rellenar los siguientes campos: Fuente, Moneda, Neto proveedor, Rentadora, Destino, Tipo de destino y Fecha Pick Up'
                          className='tooltip'
                          placement='top'
                        >
                          <Icon>info</Icon>
                        </Tooltip>
                      )}
                    </>
                  )
                }}
              </FormDataConsumer>
            </Grid>
            <Button onClick={handleClose} disabled={loadingButton} variant='outlined' color='error'>
              {translate('ra.action.cancel')}
            </Button>
          </DialogActions>
      </Dialog>
    </Form>
  )
}